import { useCallback, useEffect, useState } from 'react';
import {
  fmtINR,
  pricingintelApi,
  type BuyBoxIntel,
  type CommercialDashboard,
  type CommercialRecommendation,
  type ElasticityModel,
  type MarginAnalysis,
  type PortfolioRecord,
  type PricingRecord,
  type ProfitabilityRecord,
  type PromotionPlan,
  type ScenarioRecord,
} from '../../api/pricingintel';

type Tab = 'overview' | 'pricing' | 'profit' | 'margin' | 'promotions' | 'portfolio' | 'recommendations' | 'scenarios' | 'elasticity';

function riskClass(r: string) {
  if (r === 'critical') return 'text-red-700 bg-red-50';
  if (r === 'high') return 'text-orange-700 bg-orange-50';
  if (r === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-emerald-700 bg-emerald-50';
}

function actionClass(a: string) {
  if (a === 'scale') return 'text-emerald-700 bg-emerald-50';
  if (a === 'exit') return 'text-red-700 bg-red-50';
  if (a === 'protect') return 'text-amber-700 bg-amber-50';
  return 'text-blue-700 bg-blue-50';
}

export function PricingIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<CommercialDashboard | null>(null);
  const [pricing, setPricing] = useState<PricingRecord[]>([]);
  const [profit, setProfit] = useState<ProfitabilityRecord[]>([]);
  const [margins, setMargins] = useState<MarginAnalysis[]>([]);
  const [buybox, setBuybox] = useState<BuyBoxIntel[]>([]);
  const [elasticity, setElasticity] = useState<ElasticityModel[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioRecord[]>([]);
  const [recommendations, setRecommendations] = useState<CommercialRecommendation[]>([]);
  const [promotions, setPromotions] = useState<PromotionPlan[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, pr, pf, mg, bb, el, port, recs, promos, sc] = await Promise.all([
        pricingintelApi.dashboard(),
        pricingintelApi.listPricing(),
        pricingintelApi.listProfitability(),
        pricingintelApi.listMargins(),
        pricingintelApi.listBuyBox(),
        pricingintelApi.listElasticity(),
        pricingintelApi.listPortfolio(),
        pricingintelApi.listRecommendations(),
        pricingintelApi.listPromotions(),
        pricingintelApi.listScenarios(),
      ]);
      setDashboard(dash);
      setPricing(pr);
      setProfit(pf);
      setMargins(mg);
      setBuybox(bb);
      setElasticity(el);
      setPortfolio(port);
      setRecommendations(recs);
      setPromotions(promos);
      setScenarios(sc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pricing intelligence');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await pricingintelApi.generateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const runScenario = async (type: string, name: string) => {
    setBusy(true);
    try { await pricingintelApi.runScenario({ name, type }); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Commercial Dashboard' },
    { id: 'pricing', label: 'Pricing Workspace' },
    { id: 'profit', label: 'Profit Explorer' },
    { id: 'margin', label: 'Margin Dashboard' },
    { id: 'promotions', label: 'Promotion Planner' },
    { id: 'portfolio', label: 'Portfolio Analyzer' },
    { id: 'recommendations', label: 'Recommendation Center' },
    { id: 'scenarios', label: 'Scenario Simulator' },
    { id: 'elasticity', label: 'Elasticity Analysis' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Enterprise Pricing Intelligence</h1>
          <p className="text-sm text-slate-500">Profit optimization, commercial strategy &amp; AI copilot — BCP-00004</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Total Revenue', value: fmtINR(dashboard.total_revenue) },
              { label: 'Net Profit', value: fmtINR(dashboard.total_net_profit) },
              { label: 'Avg Margin', value: `${dashboard.avg_margin_pct.toFixed(1)}%` },
              { label: 'Buy Box Win Rate', value: `${dashboard.buy_box_win_rate_pct.toFixed(0)}%` },
              { label: 'Unprofitable SKUs', value: dashboard.unprofitable_skus.toString() },
              { label: 'Margin Erosion', value: dashboard.margin_erosion_count.toString() },
              { label: 'High Potential', value: dashboard.high_potential_count.toString() },
              { label: 'Active Recommendations', value: dashboard.active_recommendations.toString() },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-200 px-4 py-3"><h2 className="font-medium text-slate-900">Buy Box Intelligence</h2></div>
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">ASIN</th>
                  <th className="px-4 py-2">SKU</th>
                  <th className="px-4 py-2">Buy Box</th>
                  <th className="px-4 py-2">Win %</th>
                  <th className="px-4 py-2">Competitiveness</th>
                  <th className="px-4 py-2">Position</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {buybox.map((b) => (
                  <tr key={b.id}>
                    <td className="px-4 py-2">{b.asin}</td>
                    <td className="px-4 py-2 font-medium">{b.sku}</td>
                    <td className="px-4 py-2">{b.owns_buy_box ? '✓ Owned' : '✗ Lost'}</td>
                    <td className="px-4 py-2">{b.buy_box_percentage.toFixed(0)}%</td>
                    <td className="px-4 py-2">{b.price_competitiveness.toFixed(0)}/100</td>
                    <td className="px-4 py-2">#{b.offer_position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'pricing' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Selling</th>
                <th className="px-4 py-2">Buy Box</th>
                <th className="px-4 py-2">Margin</th>
                <th className="px-4 py-2">Elasticity</th>
                <th className="px-4 py-2">Gap</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pricing.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-2 font-medium">{p.sku}</td>
                  <td className="px-4 py-2">${p.selling_price.toFixed(2)}</td>
                  <td className="px-4 py-2">${p.buy_box_price?.toFixed(2) ?? '—'}</td>
                  <td className="px-4 py-2">{p.margin_pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">{p.price_elasticity?.toFixed(1) ?? '—'}</td>
                  <td className="px-4 py-2">{p.competitive_gap_pct != null ? `${p.competitive_gap_pct.toFixed(1)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'profit' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Revenue</th>
                <th className="px-4 py-2">Gross Profit</th>
                <th className="px-4 py-2">Net Profit</th>
                <th className="px-4 py-2">After Ads</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {profit.map((p) => (
                <tr key={p.id} className={p.unprofitable ? 'bg-red-50/50' : ''}>
                  <td className="px-4 py-2 font-medium">{p.sku}</td>
                  <td className="px-4 py-2">{fmtINR(p.revenue)}</td>
                  <td className="px-4 py-2">{fmtINR(p.gross_profit)}</td>
                  <td className="px-4 py-2">{fmtINR(p.net_profit)}</td>
                  <td className="px-4 py-2">{fmtINR(p.contribution_after_ads)}</td>
                  <td className="px-4 py-2">{p.unprofitable ? <span className="text-red-600 text-xs font-medium">Unprofitable</span> : <span className="text-emerald-600 text-xs">Profitable</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'margin' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {margins.map((m) => (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <h3 className="font-medium text-slate-900">{m.sku}</h3>
                <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${riskClass(m.erosion_risk)}`}>{m.erosion_risk} risk</span>
              </div>
              <dl className="mt-3 space-y-1 text-sm text-slate-600">
                <div className="flex justify-between"><dt>Gross Margin</dt><dd>{m.gross_margin_pct.toFixed(1)}%</dd></div>
                <div className="flex justify-between"><dt>Net Margin</dt><dd>{m.net_margin_pct.toFixed(1)}%</dd></div>
                <div className="flex justify-between"><dt>Trend</dt><dd className="capitalize">{m.margin_trend ?? '—'}</dd></div>
                <div className="flex justify-between"><dt>Fee Burden</dt><dd>{m.fee_burden_pct.toFixed(0)}%</dd></div>
                <div className="flex justify-between"><dt>Ad Burden</dt><dd>{m.ad_burden_pct.toFixed(0)}%</dd></div>
              </dl>
            </div>
          ))}
        </div>
      )}

      {tab === 'promotions' && (
        <div className="space-y-4">
          {promotions.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between">
                <div>
                  <span className="text-xs font-medium text-indigo-600 capitalize">{p.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900">{p.name}</h3>
                  {p.sku && <p className="text-sm text-slate-500">SKU: {p.sku}</p>}
                </div>
                <div className="text-right text-sm">
                  {p.discount_pct != null && <p>{p.discount_pct}% off</p>}
                  <p className="text-slate-500 capitalize">{p.status}</p>
                </div>
              </div>
              {p.expected_revenue != null && (
                <p className="mt-2 text-xs text-slate-500">Expected: {fmtINR(p.expected_revenue)} revenue · {fmtINR(p.expected_profit ?? 0)} profit</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'portfolio' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {portfolio.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <h3 className="font-medium text-slate-900">{p.title}</h3>
                <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${actionClass(p.action)}`}>{p.action}</span>
              </div>
              {p.rationale && <p className="mt-2 text-sm text-slate-600">{p.rationale}</p>}
              <dl className="mt-3 space-y-1 text-sm text-slate-600">
                <div className="flex justify-between"><dt>Revenue</dt><dd>{fmtINR(p.revenue ?? 0)}</dd></div>
                <div className="flex justify-between"><dt>Net Profit</dt><dd>{fmtINR(p.net_profit ?? 0)}</dd></div>
                <div className="flex justify-between"><dt>Priority</dt><dd>{p.priority_score.toFixed(0)}</dd></div>
              </dl>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <button type="button" onClick={generateRecs} disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            Generate AI Recommendations
          </button>
          {recommendations.map((rec) => (
            <div key={rec.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className="text-xs font-medium text-indigo-600">#{rec.priority_rank} · {rec.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900">{rec.title}</h3>
                  {rec.description && <p className="mt-1 text-sm text-slate-600">{rec.description}</p>}
                  {rec.evidence && <p className="mt-1 text-xs text-slate-500">{rec.evidence}</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="font-medium">{(rec.confidence_score * 100).toFixed(0)}% confidence</p>
                  {rec.suggested_price != null && rec.current_price != null && (
                    <p className="text-slate-500">${rec.current_price.toFixed(2)} → ${rec.suggested_price.toFixed(2)}</p>
                  )}
                  {rec.requires_approval && <p className="text-amber-600 text-xs">Requires approval</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { type: 'price_increase', label: 'Price +10%' },
              { type: 'price_reduction', label: 'Price -10%' },
              { type: 'advertising_increase', label: 'Ad Spend +25%' },
              { type: 'promotion_plan', label: 'Prime Day Promo' },
            ].map((s) => (
              <button key={s.type} type="button" disabled={busy} onClick={() => runScenario(s.type, s.label)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50">
                Simulate: {s.label}
              </button>
            ))}
          </div>
          {scenarios.map((sc) => (
            <div key={sc.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{sc.name}</h3>
              <p className="mt-1 text-sm text-slate-600">{sc.outcome}</p>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>Revenue: {sc.revenue_impact != null && sc.revenue_impact > 0 ? '+' : ''}{sc.revenue_impact?.toFixed(0)}%</span>
                <span>Profit: {sc.profit_impact != null && sc.profit_impact > 0 ? '+' : ''}{sc.profit_impact?.toFixed(0)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'elasticity' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Demand</th>
                <th className="px-4 py-2">Revenue</th>
                <th className="px-4 py-2">Profit</th>
                <th className="px-4 py-2">Conversion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {elasticity.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-2 font-medium">{e.sku}</td>
                  <td className="px-4 py-2">{e.demand_elasticity.toFixed(2)}</td>
                  <td className="px-4 py-2">{e.revenue_elasticity.toFixed(2)}</td>
                  <td className="px-4 py-2">{e.profit_elasticity.toFixed(2)}</td>
                  <td className="px-4 py-2">{e.conversion_elasticity.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
